// The widget kit: everything widget modules share, so each widget in this folder is one module (plus a stylesheet
// of the same name) that imports from here and from nothing in client.js. What only client.js has (the API client, the
// current webtop, redrawing, menus, dialogs, tile geometry) reaches the widgets through connect(host), which
// client.js calls before anything is drawn; the wrappers below forward to it, so widget code calls api(), render(),
// sizeOf() ... as if they were its own.
//
// A widget module's default export declares:
//   kind    the tile kind (store.js validates it in newShortcut); its stylesheet is widgets/<kind>.css
//   label   the name in the Add section and the heading of its menu
//   icon    its mark in the Add section: lineIcon('<name>') for client/icons/<name>.svg (below)
//   about   one short line saying what it shows: the tooltip in the Add section, and matched by its search box
//   look    'card' (a rounded card painted --card-bg, dragged by its header) or 'bare' (no card of its own, dragged
//           anywhere, selected with a ring); added as a class, styled in kit.css
//   sizes   the grid sizes it offers (width x height, isSize in model.js), the first being a new one's size; dragging a corner of a tile gives it any other
//   create  () => the fields a new one starts with (the server fills in the rest); the Add section places it at once with
//           these, and Edit in its menu changes them
//   render  (s, el) fills the tile element el for the record s
//   labelOf optional: (s) => the heading of this tile's menu, when it is not `label` (an applet's widget: its name)
//   menu    (s) => menu items for its right-click menu (Size, Duplicate and Delete are added after them), built from the shared
//           items below so every widget's menu reads the same: Refresh, choices, switches, Background Color, Edit;
//           an item that does not apply is false
//   fields  optional: a selector for parts of it that take pointer and key events for themselves (never drag,
//           select, nudge or delete the tile), like a note's rendered text
//   keep    optional: (s) => a signature; while it is unchanged, redraws keep the tile's element (see render)
//   fit     optional: (s, el) adjusts the body to the tile's size; runs on every layout, kept elements included
//   feed    optional: (s) => {key, load(refresh)} for content fetched from the server, with fill (s, el) to redraw
//           it and ticks: true to be refilled every minute (see "fetched data")

let client = null;
export function connect(host) { client = host; }

export const api = (...a) => client.api(...a);
export const render = (...a) => client.render(...a);
export const submenu = (...a) => client.submenu(...a);
export const colorMenu = (...a) => client.colorMenu(...a);
export const modal = (...a) => client.modal(...a);
export const toast = (...a) => client.toast(...a);
export const sizeOf = (...a) => client.sizeOf(...a);
export const sizeKey = (...a) => client.sizeKey(...a);
// The webtop's assets (applets/storage/src/assets.js): putAsset(file or address) -> {url} to write into the tile's data, assetSrc(url) -> what to show for it.
export const putAsset = (...a) => client.putAsset(...a);
export const assetSrc = (...a) => client.assetSrc(...a);
export const currentRole = () => client.currentRole();
const tiles = () => client.webtop()?.shortcuts ?? [];
const tilesOnWebtop = () => (client.webtop() ? client.onWebtop() : []);
// A tile's element on the webtop, if it is drawn.
export const tileNode = (s) => document.querySelector(`#webtop .tile[data-id="${s.id}"]`);
// A tile's address in the API (`rest` after it: '/data').
export const tilePath = (s, rest = '') => `${client.API()}/shortcuts/${s.id}${rest}`;
// Changes a tile's own fields (title, color, ...) on the server and answers the saved tile.
export const patchTile = (s, fields) => api('PATCH', tilePath(s), fields);

// UI scale (--s in style.css): geometry is worked out in unscaled px and multiplied by SCALE when drawn.
export const SCALE = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--s')) || 1;
// Menu items limited to editors (see showMenu in client.js).
export const EDIT = ['editor'];

// A widget's own state lives in `s.data` on the server (the /shortcuts/:id/data routes), so a new widget needs no
// new store fields. saveData merges keys into it (null removes one). The local copy changes first and the reply
// is not copied back, so a slow reply never overwrites what was typed since.
export async function saveData(s, patch) {
  s.data ??= {};
  for (const [k, v] of Object.entries(patch)) { if (v === null) delete s.data[k]; else s.data[k] = v; }
  try { await api('PATCH', tilePath(s, '/data'), patch); } catch (e) { toast(e); }
}

// ---- small DOM and text helpers
// An icon of the page's own: client/icons/<name>.svg, drawn in currentColor (the file is a mask, .line-icon in
// style.css), so its colour follows the text around it like inline SVG did. Markup, for innerHTML; `cls` adds classes.
export const lineIcon = (name, cls = '') => `<span class="line-icon${cls ? ` ${cls}` : ''}" aria-hidden="true"`
  + ` style="--icon: ${iconUrl(name)}"></span>`;
// An icon's address as a CSS value: the versioned url() the page shell defines as --icon-<name> (server/lib/assets.js),
// never the plain address, which would skip the cache busting.
export const iconUrl = (name) => `var(--icon-${name})`;
export const el$ = (tag, cls, text) => Object.assign(document.createElement(tag), { className: cls, ...(text != null ? { textContent: text } : {}) });
const ESC = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
export const esc = (t) => t.replace(/[&<>"']/g, (c) => ESC[c]);
export const unesc = (t) => t.replace(/&(amp|lt|gt|quot|#39);/g, (m) => Object.keys(ESC).find((k) => ESC[k] === m));
// Dark text on light colours; everything in the preset palettes is dark, custom colours may not be.
export function isLight(hex) {
  const m = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex || '');
  if (!m) return false;
  const [r, g, b] = m.slice(1).map((h) => parseInt(h, 16) / 255);
  return 0.2126 * r + 0.7152 * g + 0.0722 * b > 0.6;
}
// The width of a line of text in the system font at `size` unscaled px, for widgets that fit text to a tile.
const measure = document.createElement('canvas').getContext('2d');
export function textWidth(t, size = 10) {
  measure.font = `${size}px system-ui, sans-serif`;
  return measure.measureText(t).width;
}
// Sets a node's text only when it differs: a widget redrawn on every heartbeat writes nothing (and so has the
// browser repaint nothing) while what it shows has not changed.
export function setText(node, text) {
  if (node.textContent !== text) node.textContent = text;
}
// Puts a widget's text field into editing (a note shows its rendered text until then) and focuses it.
export function editField(ta) {
  ta.closest('.tile')?.classList.add('editing');
  ta.focus();
}

// ---- shared menu items (a widget's menu leaves out any that are false)
// Refresh: fetch the data again now, skipping the caches (anyone may, viewers of a locked webtop too).
export const refreshItem = (feed) => feed && { label: 'Refresh', action: () => loadData(feed, true) };
// A choice: a menu level of radio items that saves data[key] and redraws the webtop; `current()` is the value now.
export const choiceItem = (s, label, options, key, current) => ({ label, sub: true, roles: EDIT, action: () => radioMenu(s, label, options, key, current) });
function radioMenu(s, label, options, key, current) {
  submenu(() => [
    { label, cls: 'head' },
    ...options.map(([value, text]) => ({
      label: text, radio: true, checked: current() === value, stay: true, roles: EDIT,
      action: async () => { if (current() !== value) { await saveData(s, { [key]: value }); render(); } },
    })),
  ]);
}
// A switch: flips data[key] (drawn as a switch that keeps the menu open, showing its new state).
export const switchItem = (s, label, key, on) => ({
  label, checked: on, stay: true, roles: EDIT, action: async () => { await saveData(s, { [key]: !on }); render(); },
});
// Background Color for widgets that offer one (charts, clocks): data.color is transparent (missing, the default),
// white or black (store.js accepts only these); each widget paints it its own way when drawn.
const TILE_BACKGROUNDS = [[null, 'Transparent'], ['#ffffff', 'White'], ['#000000', 'Black']];
export const backgroundItem = (s) => choiceItem(s, 'Background Color', TILE_BACKGROUNDS, 'color', () => s.data?.color ?? null);
// Edit: the widget's dialog (formDialog), filled in by `fill(elements, s)`; what it answers is saved as {data},
// and {title, data} also renames the tile.
export const editItem = (dialog, s, fill) => ({
  label: 'Edit', quick: 'edit', roles: EDIT, action: () => editWidget(dialog, s, fill),
});
async function editWidget(dialog, s, fill) {
  const got = await dialog.open((f) => fill(f, s));
  if (!got) return;
  try {
    if ('title' in got) Object.assign(s, await patchTile(s, { title: got.title, data: { ...s.data, ...got.data } }));
    else await saveData(s, got.data);
  } catch (e) { toast(e); }
  render();
}

// ---- Edit dialogs
// formDialog(id, {title, fields, check, setup}) is a widget's options dialog. It is built on first use from
// `fields` (the markup of its labels and inputs) between a heading and the shared error line and Cancel / Save
// buttons, as <dialog id="<id>-dlg">, and every field in it is kept away from browsers' autofill and password
// managers. setup(form) runs once it is built (to fill in option lists or add listeners). open(fill) resets the
// form, lets fill(elements) put the current values in, shows it and resolves to what check(elements, busy) answers
// on Save, or null when cancelled. check throws an Error whose message is shown (the dialog stays open, see
// invalid); it may await, calling busy() first so Save reads "Checking…" meanwhile. Outside widgets: `button`
// names Save otherwise.
export function formDialog(id, { title, fields, check, setup, button = 'Save' }) {
  let dlg, form, err, save, answer = null;
  const settle = (value) => { answer?.(value); answer = null; };
  function build() {
    if (form) return;
    dlg = document.createElement('dialog');
    dlg.id = `${id}-dlg`;
    dlg.innerHTML = `<form novalidate autocomplete="off"><h2>${esc(title)}</h2>${fields}<p class="error" hidden></p>`
      + `<menu><button type="button" data-close>Cancel</button><button type="submit" class="primary">${esc(button)}</button></menu></form>`;
    form = dlg.querySelector('form');
    err = form.querySelector('.error');
    save = form.querySelector('button[type=submit]');
    for (const f of form.querySelectorAll('input, textarea')) {
      for (const [k, v] of [['autocomplete', 'off'], ['data-lpignore', 'true'], ['data-1p-ignore', ''], ['data-form-type', 'other']]) f.setAttribute(k, v);
    }
    document.body.append(dlg);
    modal(dlg);
    dlg.addEventListener('close', () => settle(null));
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      err.hidden = true;
      save.disabled = true;
      try {
        settle(await check(form.elements, () => { save.textContent = 'Checking…'; }));
        dlg.close();
      } catch (x) {
        err.textContent = x.message;
        err.hidden = false;
      } finally { save.disabled = false; save.textContent = button; }
    });
    setup?.(form);
  }
  return {
    get form() { build(); return form; },
    hideError: () => { if (err) err.hidden = true; },
    open(fill) {
      build();
      settle(null);
      form.reset();
      err.hidden = true;
      fill(form.elements);
      dlg.showModal();
      form.querySelector('input, select, textarea')?.focus();
      return new Promise((resolve) => { answer = resolve; });
    },
  };
}
export const invalid = (message) => { throw new Error(message); };
// An address as typed: "example.com/feed" or "host:port/x" become https://..., and only http(s) is accepted.
export function typedUrl(value, what) {
  let url = String(value).trim();
  if (url && !/^[a-z][a-z0-9+.-]*:\/\//i.test(url)) url = `https://${url}`;
  return /^https?:\/\/[^\s]+$/i.test(url) ? url : invalid(`Enter the address of ${what}.`);
}

// ---- a card's header: an icon and a title (where an editor drags the card from), then any buttons
export function cardHead({ icon, title, editor = currentRole() === 'editor', stale = false }, ...buttons) {
  const head = el$('div', editor ? 'card-head movable' : 'card-head');
  if (editor) head.title = 'Drag to move';
  if (stale) head.classList.add('stale');
  const i = el$('span', 'card-icon');
  i.innerHTML = icon;
  head.append(i, el$('span', 'card-title', title), ...buttons.filter(Boolean));
  return head;
}
// A message in place of a widget's content: loading, an error, nothing set yet.
export const widgetNote = (text) => el$('p', 'widget-note', text);

// ---- the heartbeat: one timer for everything on the page that changes with the clock (clocks, countdowns, the
// fetched data's minute check), instead of one per widget. heartbeat(fn, every) calls fn on the whole second, every
// `every` seconds (1 by default, the first time on the next beat; longer periods on their whole multiples, so a
// 60-second one runs on the minute), and answers a function that unsubscribes it. All of a beat's work runs in one
// animation frame, so the page is redrawn once a second however many widgets tick. The timer only runs while
// something is subscribed and the page is visible; when it comes back into view every subscriber runs at once, so
// nothing shows a stale time.
const beats = new Set(); // {fn, every, due}
let beatTimer = 0;
export function heartbeat(fn, every = 1) {
  const sub = { fn, every, due: every > 1 ? nextBeat(Date.now(), every) : 0 };
  beats.add(sub);
  startBeat();
  return () => {
    beats.delete(sub);
    if (!beats.size) stopBeat();
  };
}
const nextBeat = (now, every) => (Math.floor(now / 1000 / every) + 1) * every * 1000;
function startBeat() {
  if (!beatTimer && beats.size && !document.hidden) beatTimer = setTimeout(beat, 1000 - (Date.now() % 1000) + 5);
}
function stopBeat() {
  clearTimeout(beatTimer);
  beatTimer = 0;
}
function beat() {
  const timer = beatTimer;
  requestAnimationFrame(() => {
    if (beatTimer !== timer) return; // stopped (the page was hidden) and started again since: that timer is current
    beatTimer = 0;
    runBeats(false);
    startBeat();
  });
}
function runBeats(all) {
  const now = Date.now();
  for (const sub of [...beats]) {
    if (!beats.has(sub) || (!all && now + 50 < sub.due)) continue; // unsubscribed by an earlier one, or not yet due
    sub.due = nextBeat(now + 50, sub.every);
    try { sub.fn(); } catch (e) { console.error(e); } // one failing widget does not stop the others
  }
}
document.addEventListener('visibilitychange', () => {
  if (document.hidden) return stopBeat();
  runBeats(true);
  startBeat();
});

// ---- fetched data: widgets whose content comes from the server declare feed(s) -> {key, load(refresh)} (or null
// when there is nothing to fetch yet) and fill(s, el), which redraws the tile from it. Each key's answer is kept in
// `fetched`, shared by every tile with that key (two charts of one coin fetch once), fetched again when older than
// DATA_STALE_MS (checked every minute while the page is visible) or on request, and every tile showing it is
// refilled when it arrives (and while it loads, for a spinning refresh button).
const DATA_STALE_MS = 5 * 60 * 1000;
const fetched = new Map(); // key -> {value, at, error, pending}
const feedOf = (s) => client.widgets[s.kind]?.feed?.(s) ?? null;
// What is known for a feed: {value, at, error, pending}, all missing before its first answer.
export const fetchedFor = (feed) => (feed && fetched.get(feed.key)) || {};
export function loadData(feed, refresh = false) {
  if (!feed) return;
  const known = fetched.get(feed.key) ?? {};
  if (known.pending) return known.pending;
  const pending = feed.load(refresh)
    .then((value) => fetched.set(feed.key, { value, at: Date.now() }))
    .catch((e) => fetched.set(feed.key, { ...known, error: e.message, at: Date.now() }))
    .then(() => drawDataTiles(feed.key));
  fetched.set(feed.key, { ...known, pending });
  drawDataTiles(feed.key);
  return pending;
}
// Fetches a dialog's new data once before it is saved, so the tile has it at once; throws when the fetch fails.
export async function prefetch(feed, busy) {
  busy();
  const value = await feed.load(true);
  fetched.set(feed.key, { value, at: Date.now() });
  return value;
}
function drawDataTiles(key) {
  for (const s of tiles()) {
    if (feedOf(s)?.key !== key) continue;
    const el = tileNode(s);
    if (el) client.widgets[s.kind].fill(s, el);
  }
}
// For render(s, el): fetches the feed unless a fresh enough answer is known or on its way.
export function loadIfStale(feed) {
  const known = feed && fetched.get(feed.key);
  if (feed && !known?.pending && (!known || Date.now() - known.at > DATA_STALE_MS)) loadData(feed);
}
heartbeat(() => {
  if (!client?.webtop()) return;
  new Map(tilesOnWebtop().map(feedOf).filter(Boolean).map((f) => [f.key, f])).forEach(loadIfStale);
  // widgets that say how old their data is (`ticks`) are refilled, so "2 min. ago" keeps up
  for (const s of tilesOnWebtop()) {
    const el = client.widgets[s.kind]?.ticks && tileNode(s);
    if (el) client.widgets[s.kind].fill(s, el);
  }
}, 60);
// The refresh button of a fetched widget: spins while loading, says when the data was fetched.
export function refreshButton(feed, { fetchedAt, pending } = {}) {
  const b = el$('button', pending ? 'card-refresh loading' : 'card-refresh');
  b.type = 'button';
  b.title = fetchedAt ? `Refresh (updated ${ago(fetchedAt)})` : 'Refresh';
  b.setAttribute('aria-label', 'Refresh');
  b.innerHTML = lineIcon('refresh');
  b.disabled = !feed;
  b.addEventListener('click', () => loadData(feed, true));
  return b;
}
const agoFmt = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto', style: 'short' });
// How long ago (or until) an ISO time is, in the browser's language: "3 min. ago".
export function ago(iso) {
  const sec = (Date.parse(iso) - Date.now()) / 1000;
  if (Number.isNaN(sec)) return '';
  for (const [unit, n] of [['year', 31536000], ['month', 2592000], ['day', 86400], ['hour', 3600], ['minute', 60]]) {
    if (Math.abs(sec) >= n) return agoFmt.format(Math.round(sec / n), unit);
  }
  return agoFmt.format(0, 'minute');
}
